// Pure helpers for the API key rules form and table. `@/…` imports are type-only so
// this module runs under `node --experimental-strip-types`.
import type { APIKeyRules } from '@/types/apiKey';

/** Splits the IP textarea into entries: newline or comma separated, trimmed, blanks dropped. */
export function parseIPLines(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function formatIPLines(ips: string[]): string {
  return ips.join('\n');
}

/** Highest rate limit the server accepts. */
export const MAX_RATE_LIMIT_RPM = 1_000_000;

/** '' = unlimited (null); an integer from 1 to MAX_RATE_LIMIT_RPM = the limit; anything else is invalid (undefined). */
export function parseRateLimit(text: string): number | null | undefined {
  const s = text.trim();
  if (s === '') return null;
  if (!/^\d+$/.test(s)) return undefined;
  const n = Number(s);
  return n > 0 && n <= MAX_RATE_LIMIT_RPM ? n : undefined;
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/** Short labels summarizing a key's rules for the keys table. */
export function ruleBadges(k: APIKeyRules): string[] {
  const out: string[] = [];
  if (k.own_apps_only) out.push('Own apps only');
  if (k.allowed_domain_ids.length) out.push(plural(k.allowed_domain_ids.length, 'domain'));
  if (k.allowed_catalog_stack_ids.length) out.push(plural(k.allowed_catalog_stack_ids.length, 'stack'));
  if (k.ip_allowlist.length) out.push('IP-restricted');
  if (k.rate_limit_rpm != null) out.push(`${k.rate_limit_rpm} rpm`);
  return out.length ? out : ['Unrestricted'];
}

const pad = (n: number) => String(n).padStart(2, '0');

/** A stored expiry (RFC3339) as a datetime-local value in the browser's time zone; '' when unset or unparseable. */
export function toDateTimeLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * A datetime-local value as RFC3339 UTC for the API; undefined (= never expires) when empty.
 * The server reads a zone-less `YYYY-MM-DDTHH:MM` as UTC, so the local value must not be sent as is.
 */
export function fromDateTimeLocal(value: string): string | undefined {
  if (!value) return undefined;
  const d = new Date(value); // a zone-less date-time string parses as local time
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/**
 * The expires_at to send: the stored value untouched while the field still shows it (the
 * input drops seconds), otherwise the field's value. `original` is null for a new key.
 */
export function expiryForSubmit(value: string, original: string | null): string | undefined {
  if (original && value === toDateTimeLocal(original)) return original;
  return fromDateTimeLocal(value);
}

type Allowlists = Pick<APIKeyRules, 'allowed_domain_ids' | 'allowed_catalog_stack_ids'>;

/** Warning when an edit empties an allowlist that had entries, which widens it to everything; null otherwise. */
export function emptiedAllowlistWarning(before: Allowlists, after: Allowlists): string | null {
  const domains = before.allowed_domain_ids.length > 0 && after.allowed_domain_ids.length === 0;
  const stacks = before.allowed_catalog_stack_ids.length > 0 && after.allowed_catalog_stack_ids.length === 0;
  const confirm = ' Save again to confirm.';
  if (domains && stacks) {
    return 'The domain and catalog stack allowlists are now empty, which allows every domain and every catalog stack.' + confirm;
  }
  if (domains) return 'The domain allowlist is now empty, which allows every domain.' + confirm;
  if (stacks) return 'The catalog stack allowlist is now empty, which allows every catalog stack.' + confirm;
  return null;
}
